import { getDefaultViewData } from '@realestate.com.au/iglu-data';
import tealiumWrapper from '../../../shared/tracking/tealium/tealiumWrapper';
import { constructUserData } from '../../../shared/tracking/tealium/trackingUtils';
import {
  TEALIUM_SITE,
  TEALIUM_SCHEMA_IN_PAGE_NAVIGATION,
  TEALIUM_SCHEMA_NEARBY_LOCATION_SELECTED,
  TEALIUM_SCHEMA_EXPERIMENTS,
  TEALIUM_SCHEMA_CAROUSEL_INTERACTION,
  PAGE_TYPE_HOME_PAGE,
  FIND_AGENT_SITE_SECTION,
  TEALIUM_SCHEMA_CAROUSEL_IMPRESSION,
} from '../../../shared/tracking/tealium/constants';
import {
  getTrackingSource,
  setTrackingSource,
} from '../../../client/tracking/tealium/sourceTracking';

const buildSitSection = (channelObject) =>
  (channelObject ? `${channelObject.tealium || FIND_AGENT_SITE_SECTION}` : '');

const buildPageSubSection = (channelObject, homepageType) => {
  const siteSection = buildSitSection(channelObject);
  return `${TEALIUM_SITE}${siteSection ? `:${siteSection}` : ''}:${homepageType}`;
};

const trackPageEvent = ({ channelObject, experimentsManager, homepageType }) => {
  const maybeExperiments = experimentsManager?.getAllExperimentsForTracking();
  const { element, page, type } = getTrackingSource();
  const siteSection = buildSitSection(channelObject);
  const defaultViewData = getDefaultViewData({
    page: {
      pageNameOverride: `${TEALIUM_SITE}${siteSection ? `:${siteSection}` : ''}:${homepageType}`,
      site: TEALIUM_SITE,
      pageType: homepageType,
      siteSection,
      siteSubSection: buildPageSubSection(channelObject, homepageType),
      clickThroughSourceElement: element,
      clickThroughSourcePage: page,
      clickThroughSourceType: type,
    },
  });

  const experimentsData = () => ({
    experiments: {
      schema: TEALIUM_SCHEMA_EXPERIMENTS,
      data: {
        experiments: maybeExperiments,
      },
    },
  });

  tealiumWrapper.trackView([
    ...defaultViewData,
    constructUserData,
    ...(maybeExperiments ? [experimentsData] : []),
  ]);
};

const searchAreaUserAction = ({ userAction }) => {
  tealiumWrapper.trackEvent(() => ({
    schema: TEALIUM_SCHEMA_NEARBY_LOCATION_SELECTED,
    data: {
      event_name: 'nearby_location_selected',
      user_action: userAction,
    },
  }));
};

const trackAreaAndRegionSelectorClick = ({ areaId, regionId }) => {
  const normalisedAreaId = areaId.toLowerCase();
  const areaAndRegion = regionId === 'any' ? normalisedAreaId : `${normalisedAreaId}-${regionId}`;
  tealiumWrapper.trackEvent(() => ({
    schema: TEALIUM_SCHEMA_IN_PAGE_NAVIGATION,
    data: {
      event_name: 'in_page_navigation',
      user_action: `area_selector_click:${areaAndRegion}`,
    },
  }));
};

const trackRecentSearchClick = ({ channelObject, homepageType }) => {
  const page = buildPageSubSection(channelObject, homepageType);
  setTrackingSource({ page, element: 'recent search click' });
};

const trackChannelTabClick = ({ channelObject, homepageType }) => {
  const page = buildPageSubSection(channelObject, homepageType);
  setTrackingSource({ page, element: 'channel-search-tab' });
};

const trackSearchClick = ({ channelObject, homepageType, trackingSource }) => {
  const page = buildPageSubSection(channelObject, homepageType);
  setTrackingSource({ page, element: `${trackingSource}-search-button` });
};

const trackAreaItemClick = ({ channelObject, homepageType, itemId }) => {
  setTrackingSource({
    page: buildPageSubSection(channelObject, homepageType),
    element: `area_click:${itemId}`,
  });
};

const trackPropertyTypeClick = ({ channelObject, homepageType }) => {
  const page = buildPageSubSection(channelObject, homepageType);
  setTrackingSource({ page, element: 'property_type_tile' });
};

const trackExploreOthersItemClick = ({ exploreOthersId }) => {
  setTrackingSource({
    page: buildPageSubSection(undefined, PAGE_TYPE_HOME_PAGE),
    element: `explore-others:${exploreOthersId}`,
  });
};

const trackCarouselInteraction = ({ inPageSource }) => {
  tealiumWrapper.trackEvent(() => ({
    schema: TEALIUM_SCHEMA_CAROUSEL_INTERACTION,
    data: {
      event_name: 'carousel_interaction',
      in_page_source: inPageSource,
      user_action: 'swipe',
    },
  }));
};

const trackElitePlusCarouselImpressionEvent = (listings, bucket) => {
  const impressionListings = listings.map((listing, index) => ({
    listing_id: listing.id,
    position: index + 1,
  }));
  tealiumWrapper.trackEvent(() => ({
    schema: TEALIUM_SCHEMA_CAROUSEL_IMPRESSION,
    data: {
      event_name: 'carousel_impression',
      product_type: bucket === 'featured' ? 'explore_new_properties' : 'suggested_properties',
      listings_total: listings.length,
      listings: impressionListings,
    },
  }));
};
const trackElitePlusCarouselListingClick = (bucket) => {
  setTrackingSource({
    page: buildPageSubSection(undefined, PAGE_TYPE_HOME_PAGE),
    element: bucket === 'featured' ? 'explore_new_properties' : 'suggested_properties',
  });
};

export {
  buildPageSubSection,
  trackPageEvent,
  trackAreaAndRegionSelectorClick,
  searchAreaUserAction,
  trackRecentSearchClick,
  trackAreaItemClick,
  trackPropertyTypeClick,
  trackExploreOthersItemClick,
  trackChannelTabClick,
  trackSearchClick,
  trackCarouselInteraction,
  trackElitePlusCarouselImpressionEvent,
  trackElitePlusCarouselListingClick,
};
